import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface Run {
  id: string; status: string; score: number | null; pagesCrawled: number; probesRun: number
  createdAt: string; completedAt: string | null
  site: { id: string; domain: string; tenant: { id: string; name: string } }
}

export function AdminRunsPage() {
  const t = useT()
  const [statusFilter, setStatusFilter] = useState('')
  const runs = useQuery({
    queryKey: ['admin-runs', statusFilter],
    queryFn: () => {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      return apiClient.get<{ data: Run[] }>(`/admin/runs?${params}`)
    },
    refetchInterval: 15_000,
  })

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">{t('admin.runs.title')}</h1>
      <div className="mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 text-sm border rounded bg-transparent"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <option value="">{t('admin.runs.allstatus')}</option>
          <option value="queued">queued</option>
          <option value="running">running</option>
          <option value="completed">completed</option>
          <option value="failed">failed</option>
          <option value="cancelled">cancelled</option>
        </select>
      </div>
      {runs.isLoading && <p>{t('common.loading')}</p>}
      {runs.data && (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left opacity-70">
              <th className="py-2 pr-4">{t('admin.runs.column.site')}</th>
              <th className="py-2 pr-4">{t('admin.runs.column.tenant')}</th>
              <th className="py-2 pr-4">{t('admin.runs.column.status')}</th>
              <th className="py-2 pr-4">{t('admin.runs.column.score')}</th>
              <th className="py-2 pr-4">{t('admin.runs.column.pages')}</th>
              <th className="py-2 pr-4">{t('admin.runs.column.probes')}</th>
              <th className="py-2 pr-4">{t('admin.runs.column.created')}</th>
            </tr>
          </thead>
          <tbody>
            {runs.data.data.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="py-2 pr-4">{r.site.domain}</td>
                <td className="py-2 pr-4">
                  <Link to={`/admin/tenants/${r.site.tenant.id}`} className="hover:underline">{r.site.tenant.name}</Link>
                </td>
                <td className="py-2 pr-4">{r.status}</td>
                <td className="py-2 pr-4">{r.score ?? '—'}</td>
                <td className="py-2 pr-4">{r.pagesCrawled}</td>
                <td className="py-2 pr-4">{r.probesRun}</td>
                <td className="py-2 pr-4">{new Date(r.createdAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
